import { Command } from 'commander'
import { spawn } from 'node:child_process'
import { mkdir, writeFile } from 'node:fs/promises'
import { basename, join } from 'node:path'
import { PACKAGE_VERSION } from '../version.js'
import {
  claudeMcpAddArgs,
  claudeMcpRemoveArgs,
  normalizeAgentHost,
  renderAgentInstall,
  type AgentHost,
} from './agent-config.js'
import { renderDoctor, runDoctor } from './doctor.js'
import { listPrompts, renderPrompt } from './prompts.js'
import { workflowOutputBaseDir } from '../workflows/artifact-writer.js'
import { findWorkflowReport, listWorkflowDefinitions, listWorkflowReports, openWorkflowReport, runWorkflow } from '../workflows/registry.js'

function numberOpt(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function booleanOpt(value: unknown): boolean | undefined {
  if (value === undefined) return undefined
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

function compactInput(input: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined))
}

function workflowInput(id: string, opts: Record<string, unknown>): Record<string, unknown> {
  if (id === 'agent-packet' || id === 'serp-comparison' || id === 'ai-overview-language') {
    return compactInput({
      keyword: opts.keyword,
      domain: opts.domain,
      url: opts.url,
      location: opts.location,
      maxResults: numberOpt(opts.maxResults),
      maxQuestions: numberOpt(opts.maxQuestions),
      extractTop: numberOpt(opts.extractTop),
      includeSerp: opts.serp === false ? false : undefined,
      includePaa: opts.paa === false ? false : undefined,
      includeAiOverview: booleanOpt(opts.includeAiOverview),
      returnPartial: opts.returnPartial === false ? false : undefined,
    })
  }

  if (id === 'paa-expansion-brief') {
    return compactInput({
      keyword: opts.keyword,
      location: opts.location,
      maxQuestions: numberOpt(opts.maxQuestions),
      depth: numberOpt(opts.depth),
      returnPartial: opts.returnPartial === false ? false : undefined,
    })
  }

  if (id === 'directory' || id === 'local-competitive-audit' || id === 'map-comparison') {
    return compactInput({
      query: opts.query,
      location: opts.location,
      state: opts.state,
      minPopulation: numberOpt(opts.minPop ?? opts.minPopulation),
      maxCities: numberOpt(opts.maxCities),
      maxResultsPerCity: numberOpt(opts.perCity ?? opts.maxResultsPerCity ?? opts.maxResults),
      concurrency: numberOpt(opts.concurrency),
      proxyMode: opts.proxyMode,
      hydrateTop: id === 'local-competitive-audit' || id === 'map-comparison' ? numberOpt(opts.hydrateTop) : undefined,
      maxReviews: id === 'local-competitive-audit' || id === 'map-comparison' ? numberOpt(opts.reviews ?? opts.maxReviews) : undefined,
      returnPartial: opts.returnPartial === false ? false : undefined,
    })
  }

  return compactInput(opts)
}

function writeOutput(data: unknown, json: boolean | undefined): void {
  if (json) {
    process.stdout.write(`${JSON.stringify(data, null, 2)}\n`)
  } else if (typeof data === 'string') {
    process.stdout.write(`${data}\n`)
  } else {
    process.stdout.write(`${JSON.stringify(data, null, 2)}\n`)
  }
}

function maskSecrets(value: string): string {
  return value.replace(/sk_[A-Za-z0-9_-]+/g, 'sk_***')
}

function runLocalCommand(command: string, args: string[]): Promise<{ code: number; stdout: string; stderr: string }> {
  return new Promise((resolve) => {
    const child = spawn(command, args, { stdio: ['ignore', 'pipe', 'pipe'] })
    const stdout: Buffer[] = []
    const stderr: Buffer[] = []
    let settled = false
    const done = (result: { code: number; stdout: string; stderr: string }) => {
      if (settled) return
      settled = true
      resolve(result)
    }
    child.stdout.on('data', chunk => stdout.push(Buffer.from(chunk)))
    child.stderr.on('data', chunk => stderr.push(Buffer.from(chunk)))
    child.on('error', err => {
      done({ code: 127, stdout: '', stderr: err.message })
    })
    child.on('close', code => {
      done({
        code: typeof code === 'number' ? code : 1,
        stdout: Buffer.concat(stdout).toString('utf8'),
        stderr: Buffer.concat(stderr).toString('utf8'),
      })
    })
  })
}

function cadenceOpt(opts: Record<string, unknown>): 'daily' | 'weekly' | 'monthly' {
  if (opts.daily) return 'daily'
  if (opts.monthly) return 'monthly'
  return 'weekly'
}

function apiOptions(opts: Record<string, unknown>): { apiUrl: string; apiKey: string } {
  const apiKey = String(opts.apiKey ?? process.env.MCP_SCRAPER_API_KEY ?? '').trim()
  if (!apiKey) throw new Error('MCP_SCRAPER_API_KEY is required. Pass --api-key or set the environment variable.')
  return {
    apiUrl: String(opts.apiUrl ?? process.env.MCP_SCRAPER_API_URL ?? 'https://mcpscraper.dev').replace(/\/$/, ''),
    apiKey,
  }
}

function openExternalUrl(url: string): boolean {
  const command = process.platform === 'darwin'
    ? 'open'
    : process.platform === 'win32'
      ? 'cmd'
      : 'xdg-open'
  const args = process.platform === 'win32' ? ['/c', 'start', '', url] : [url]
  try {
    const child = spawn(command, args, { detached: true, stdio: 'ignore' })
    child.unref()
    return true
  } catch {
    return false
  }
}

async function apiRequest<T>(path: string, method: string, opts: Record<string, unknown>, body?: unknown): Promise<T> {
  const { apiUrl, apiKey } = apiOptions(opts)
  const res = await fetch(`${apiUrl}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
    body: body == null ? undefined : JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = typeof (data as { error?: unknown }).error === 'string'
      ? (data as { error: string }).error
      : `API request failed with ${res.status}`
    throw new Error(message)
  }
  return data as T
}

function addWorkflowInputOptions(command: Command): Command {
  return command
    .option('--keyword <keyword>', 'Agent packet keyword')
    .option('--domain <domain>', 'Target domain')
    .option('--url <url>', 'Target page URL')
    .option('--location <location>', 'Target location')
    .option('--max-results <n>', 'Maximum SERP or Maps results')
    .option('--max-questions <n>', 'Maximum PAA questions')
    .option('--extract-top <n>', 'Top result pages or citation pages to extract')
    .option('--depth <n>', 'PAA expansion depth')
    .option('--no-serp', 'Skip SERP evidence for agent packet')
    .option('--no-paa', 'Skip PAA evidence for agent packet')
    .option('--query <query>', 'Business category or workflow query')
    .option('--state <state>', 'US state')
    .option('--min-pop <n>', 'Minimum city population')
    .option('--max-cities <n>', 'Maximum selected cities')
    .option('--per-city <n>', 'Maps results per city')
    .option('--concurrency <n>', 'City search concurrency')
    .option('--proxy-mode <mode>', 'Proxy mode: location, configured, or none')
    .option('--hydrate-top <n>', 'Profiles to hydrate per city for competitive audits')
    .option('--reviews <n>', 'Review cards to collect per hydrated profile')
    .option('--no-return-partial', 'Fail instead of writing partial artifacts')
}

export function buildHumanCli(): Command {
  const program = new Command()
  program
    .name('mcp-scraper-cli')
    .description('Human CLI for MCP Scraper setup, workflows, reports, and agent-ready SEO artifacts.')
    .version(PACKAGE_VERSION)

  program.command('doctor')
    .description('Check setup, API key, output directory, package version, and recommended MCP config.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--output-dir <path>', 'Output directory to test')
    .option('--json', 'Print machine-readable JSON')
    .action(async (opts) => {
      const output = await runDoctor({ apiKey: opts.apiKey, apiUrl: opts.apiUrl, outputDir: opts.outputDir })
      writeOutput(opts.json ? output : renderDoctor(output), opts.json)
      if (!output.ok) process.exitCode = 1
    })

  const billing = program.command('billing').description('Inspect billing and start checkout flows.')
  const billingConcurrency = billing.command('concurrency').description('Manage MCP Scraper concurrency slots.')

  billingConcurrency.command('info')
    .description('Show current concurrency limit and extra-slot price.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (opts) => {
      const result = await apiRequest<{ concurrency: Record<string, unknown> }>('/billing/credits', 'POST', opts, {})
      if (opts.json) writeOutput(result.concurrency, true)
      else {
        const upgrade = result.concurrency?.upgrade as Record<string, unknown> | undefined
        writeOutput([
          `Current limit: ${result.concurrency?.current_limit ?? 'unknown'} concurrent operations`,
          `Extra slots: ${result.concurrency?.current_extra_slots ?? 'unknown'}`,
          `Extra concurrency slot: ${upgrade?.price_label ?? '$5/month'}`,
          `Upgrade command: ${upgrade?.terminal_command ?? 'mcp-scraper-cli billing concurrency checkout'}`,
        ].join('\n'), false)
      }
    })

  billingConcurrency.command('checkout')
    .description('Create a hosted Stripe checkout for one extra concurrency slot.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .option('--no-open', 'Print the checkout URL without opening a browser')
    .action(async (opts) => {
      const result = await apiRequest<{ checkout_url: string; price?: { price_label?: string }; next_step?: string }>('/billing/concurrency/terminal-checkout', 'POST', opts, {})
      if (opts.json) {
        writeOutput(result, true)
        return
      }
      const opened = opts.open !== false && openExternalUrl(result.checkout_url)
      writeOutput([
        `Extra concurrency slot: ${result.price?.price_label ?? '$5/month'}`,
        opened ? `Opened checkout: ${result.checkout_url}` : `Checkout URL: ${result.checkout_url}`,
        result.next_step ?? 'Complete checkout, then retry the MCP request.',
      ].join('\n'), false)
    })

  billing.command('subscribe <tier>')
    .description('Subscribe to a plan (starter | growth | scale) via a hosted Stripe checkout link.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .option('--no-open', 'Print the checkout URL without opening a browser')
    .action(async (tier, opts) => {
      const result = await apiRequest<{ checkout_url?: string; updated?: boolean; tier?: string; label?: string; monthly_usd?: number; credits_per_month?: number; concurrency?: number; intro?: string | null; message?: string; next_step?: string }>('/billing/subscribe/terminal-checkout', 'POST', opts, { tier: String(tier).toLowerCase() })
      if (opts.json) { writeOutput(result, true); return }
      if (result.updated) { writeOutput(result.message ?? `Switched to ${result.tier}.`, false); return }
      const opened = opts.open !== false && !!result.checkout_url && openExternalUrl(result.checkout_url)
      writeOutput([
        `Plan: ${result.label ?? tier} — $${result.monthly_usd}/mo · ${result.credits_per_month?.toLocaleString()} credits/mo · ${result.concurrency} concurrency${result.intro ? ` · ${result.intro}` : ''}`,
        opened ? `Opened checkout: ${result.checkout_url}` : `Checkout URL: ${result.checkout_url}`,
        result.next_step ?? 'Complete payment in the browser.',
      ].join('\n'), false)
    })

  const agent = program.command('agent').description('Generate AI-agent install configs and workflow prompts.')
  agent.command('install <host>')
    .description('Print or apply install/config instructions for codex, claude/claude-code, or claude-desktop.')
    .option('--api-key <key>', 'API key to place in generated config')
    .option('--package <spec>', 'npm package spec', 'mcp-scraper@latest')
    .option('--browser-profile <name>', 'Default saved browser profile for browser_open sessions.')
    .option('--save-browser-profile-changes', 'Persist cookies and browser storage back to the named browser profile when sessions close')
    .option('--apply', 'Apply the config to this client when supported. For Claude Code, this upserts the user-scope mcp-scraper server.')
    .option('--json', 'Print machine-readable JSON')
    .action(async (hostInput: AgentHost, opts) => {
      const host = normalizeAgentHost(hostInput)
      const apiKey = opts.apiKey ?? process.env.MCP_SCRAPER_API_KEY
      if (opts.apply) {
        if (host !== 'claude') throw new Error('--apply is currently supported for Claude Code only. Use host "claude" or "claude-code".')
        if (!apiKey?.trim()) {
          throw new Error('MCP_SCRAPER_API_KEY is required for --apply. Set it in the environment or pass --api-key.')
        }

        const configOptions = {
          apiKey,
          packageSpec: opts.package,
          browserProfileName: opts.browserProfile,
          browserProfileSaveChanges: opts.saveBrowserProfileChanges,
        }
        const remove = await runLocalCommand('claude', claudeMcpRemoveArgs())
        const add = await runLocalCommand('claude', claudeMcpAddArgs(configOptions))
        if (add.code !== 0) {
          const reason = maskSecrets([add.stderr, add.stdout].filter(Boolean).join('\n').trim())
          throw new Error([
            'Claude Code MCP registration failed.',
            reason || 'No error output returned.',
            'Make sure Claude Code is installed and the `claude` command is on PATH.',
          ].join('\n'))
        }
        const list = await runLocalCommand('claude', ['mcp', 'list'])
        const result = {
          host: 'claude',
          applied: true,
          replacedExisting: remove.code === 0,
          command: 'npx',
          args: ['-y', '-p', opts.package, 'mcp-scraper'],
          nextStep: 'Fully exit Claude Code, start a new Claude terminal, then run: claude mcp list',
          list: maskSecrets([list.stdout, list.stderr].filter(Boolean).join('\n').trim()),
        }
        if (opts.json) {
          writeOutput(result, true)
          return
        }
        writeOutput([
          'Applied Claude Code MCP config: mcp-scraper',
          remove.code === 0 ? 'Replaced existing mcp-scraper entry.' : 'No existing mcp-scraper entry found; added a new one.',
          'Command: npx -y -p ' + opts.package + ' mcp-scraper',
          opts.browserProfile ? `Browser profile: ${opts.browserProfile}` : '',
          '',
          'Next step: fully exit Claude Code, start a new Claude terminal, then run:',
          '  claude mcp list',
          '',
          result.list ? `Current Claude MCP list:\n${result.list}` : '',
        ].filter(Boolean).join('\n'), false)
        return
      }
      const text = renderAgentInstall(host, {
        apiKey: opts.apiKey,
        packageSpec: opts.package,
        browserProfileName: opts.browserProfile,
        browserProfileSaveChanges: opts.saveBrowserProfileChanges,
      })
      writeOutput(opts.json ? { host, text } : text, opts.json)
    })

  agent.command('prompt [name]')
    .description('Print an agent prompt template.')
    .option('--json', 'Print machine-readable JSON')
    .action((name: string | undefined, opts) => {
      if (!name) {
        writeOutput(opts.json ? { prompts: listPrompts() } : listPrompts().join('\n'), opts.json)
        return
      }
      const text = renderPrompt(name)
      writeOutput(opts.json ? { name, text } : text, opts.json)
    })

  const workflow = program.command('workflow').description('Run named SEO workflows.')
  workflow.command('list')
    .description('List available workflows.')
    .option('--json', 'Print machine-readable JSON')
    .action((opts) => {
      const rows = listWorkflowDefinitions()
      if (opts.json) writeOutput({ workflows: rows }, true)
      else writeOutput(rows.map(row => `${row.id}\t${row.title}\n  ${row.description}`).join('\n'), false)
    })

  workflow.command('run <id>')
    .description('Run a workflow and save local artifacts.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--output-dir <path>', 'Workflow output directory')
    .option('--json', 'Print machine-readable JSON')
    .option('--keyword <keyword>', 'Agent packet keyword')
    .option('--domain <domain>', 'Target domain')
    .option('--url <url>', 'Target page URL')
    .option('--location <location>', 'Target location')
    .option('--max-results <n>', 'Maximum SERP or Maps results')
    .option('--max-questions <n>', 'Maximum PAA questions')
    .option('--extract-top <n>', 'Top result pages or citation pages to extract')
    .option('--depth <n>', 'PAA expansion depth')
    .option('--no-serp', 'Skip SERP evidence for agent packet')
    .option('--no-paa', 'Skip PAA evidence for agent packet')
    .option('--query <query>', 'Business category or workflow query')
    .option('--state <state>', 'US state')
    .option('--min-pop <n>', 'Minimum city population')
    .option('--max-cities <n>', 'Maximum selected cities')
    .option('--per-city <n>', 'Maps results per city')
    .option('--concurrency <n>', 'City search concurrency')
    .option('--proxy-mode <mode>', 'Proxy mode: location, configured, or none')
    .option('--hydrate-top <n>', 'Profiles to hydrate per city for competitive audits')
    .option('--reviews <n>', 'Review cards to collect per hydrated profile')
    .option('--no-return-partial', 'Fail instead of writing partial artifacts')
    .action(async (id: string, opts) => {
      const summary = await runWorkflow(id, workflowInput(id, opts), {
        apiKey: opts.apiKey,
        apiUrl: opts.apiUrl,
        outputDir: opts.outputDir,
      })
      if (opts.json) writeOutput(summary, true)
      else {
        writeOutput([
          `${summary.title}: ${summary.status}`,
          summary.summary,
          summary.reportPath ? `Report: ${summary.reportPath}` : '',
          summary.warnings.length ? `Warnings:\n${summary.warnings.map(w => `- ${w}`).join('\n')}` : '',
        ].filter(Boolean).join('\n'), false)
      }
    })

  const report = program.command('report').description('List and open local workflow reports.')
  report.command('list')
    .description('List recent workflow reports.')
    .option('--output-dir <path>', 'Workflow output directory')
    .option('--json', 'Print machine-readable JSON')
    .action(async (opts) => {
      const reports = await listWorkflowReports(opts.outputDir)
      writeOutput(opts.json ? { reports } : reports.map(r => `${r.startedAt}\t${r.workflow}\t${r.status}\t${r.reportPath ?? r.manifestPath}`).join('\n'), opts.json)
    })
  report.command('path [id]')
    .description('Print a report path. Defaults to last.')
    .option('--output-dir <path>', 'Workflow output directory')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id = 'last', opts) => {
      const found = await findWorkflowReport(id, opts.outputDir)
      if (!found?.reportPath) throw new Error(`No report found for "${id}"`)
      writeOutput(opts.json ? { path: found.reportPath, run: found } : found.reportPath, opts.json)
    })
  report.command('open [id]')
    .description('Open a report. Defaults to last.')
    .option('--output-dir <path>', 'Workflow output directory')
    .option('--json', 'Print machine-readable JSON instead of opening')
    .action(async (id = 'last', opts) => {
      if (opts.json) {
        const found = await findWorkflowReport(id, opts.outputDir)
        if (!found?.reportPath) throw new Error(`No report found for "${id}"`)
        writeOutput({ path: found.reportPath, run: found }, true)
        return
      }
      const path = await openWorkflowReport(id, opts.outputDir)
      writeOutput(`Opened: ${path}`, false)
    })

  const schedule = program.command('schedule').description('Create and manage hosted workflow schedules.')
  addWorkflowInputOptions(schedule.command('create <workflowId>')
    .description('Create a recurring hosted workflow schedule.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--name <name>', 'Schedule name')
    .option('--daily', 'Run daily')
    .option('--weekly', 'Run weekly')
    .option('--monthly', 'Run monthly')
    .option('--timezone <tz>', 'Schedule timezone', 'UTC')
    .option('--webhook <url>', 'HTTPS webhook URL')
    .option('--next-run-at <iso>', 'First run time as an ISO timestamp')
    .option('--json', 'Print machine-readable JSON'))
    .action(async (workflowId: string, opts) => {
      const result = await apiRequest('/workflows/schedules', 'POST', opts, {
        workflowId,
        name: opts.name,
        input: workflowInput(workflowId, opts),
        cadence: cadenceOpt(opts),
        timezone: opts.timezone,
        webhookUrl: opts.webhook,
        nextRunAt: opts.nextRunAt,
      })
      writeOutput(result, opts.json)
    })

  schedule.command('list')
    .description('List hosted workflow schedules.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (opts) => {
      const result = await apiRequest<{ schedules: Array<Record<string, unknown>> }>('/workflows/schedules', 'GET', opts)
      if (opts.json) writeOutput(result, true)
      else writeOutput(result.schedules.map(scheduleRow => `${scheduleRow.id}\t${scheduleRow.status}\t${scheduleRow.workflow_id}\t${scheduleRow.next_run_at ?? ''}`).join('\n'), false)
    })

  schedule.command('pause <id>')
    .description('Pause a hosted workflow schedule.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id: string, opts) => writeOutput(await apiRequest(`/workflows/schedules/${id}`, 'PATCH', opts, { status: 'paused' }), opts.json))

  schedule.command('resume <id>')
    .description('Resume a hosted workflow schedule.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id: string, opts) => writeOutput(await apiRequest(`/workflows/schedules/${id}`, 'PATCH', opts, { status: 'active' }), opts.json))

  schedule.command('delete <id>')
    .description('Delete a hosted workflow schedule.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id: string, opts) => writeOutput(await apiRequest(`/workflows/schedules/${id}`, 'DELETE', opts), opts.json))

  schedule.command('run <id>')
    .description('Run a hosted workflow schedule now.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id: string, opts) => writeOutput(await apiRequest(`/workflows/schedules/${id}/run`, 'POST', opts, {}), opts.json))

  const runs = program.command('runs').description('Inspect and download hosted workflow runs.')
  runs.command('list')
    .description('List hosted workflow runs.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (opts) => {
      const result = await apiRequest<{ runs: Array<Record<string, unknown>> }>('/workflows/runs', 'GET', opts)
      if (opts.json) writeOutput(result, true)
      else writeOutput(result.runs.map(run => `${run.id}\t${run.status}\t${run.workflow_id}\t${run.queued_at}`).join('\n'), false)
    })

  runs.command('status <id>')
    .description('Show a hosted workflow run.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id: string, opts) => writeOutput(await apiRequest(`/workflows/runs/${id}`, 'GET', opts), opts.json))

  runs.command('download <id>')
    .description('Download hosted workflow run artifacts.')
    .option('--api-key <key>', 'MCP Scraper API key')
    .option('--api-url <url>', 'MCP Scraper API URL', 'https://mcpscraper.dev')
    .option('--output-dir <path>', 'Download directory')
    .option('--json', 'Print machine-readable JSON')
    .action(async (id: string, opts) => {
      const result = await apiRequest<{ run: { id: string; artifacts?: Array<{ id: string; label: string; path: string }> } }>(`/workflows/runs/${id}`, 'GET', opts)
      const { apiUrl, apiKey } = apiOptions(opts)
      const outDir = join(opts.outputDir ?? workflowOutputBaseDir(), 'workflow-downloads', id)
      await mkdir(outDir, { recursive: true })
      const downloaded: string[] = []
      for (const artifact of result.run.artifacts ?? []) {
        const res = await fetch(`${apiUrl}/workflows/runs/${id}/artifacts/${artifact.id}`, { headers: { 'x-api-key': apiKey } })
        if (!res.ok) throw new Error(`Failed to download ${artifact.label}: HTTP ${res.status}`)
        const file = join(outDir, basename(artifact.path))
        await writeFile(file, Buffer.from(await res.arrayBuffer()))
        downloaded.push(file)
      }
      writeOutput(opts.json ? { runId: id, files: downloaded } : downloaded.join('\n'), opts.json)
    })

  return program
}

export async function runHumanCli(argv = process.argv): Promise<void> {
  await buildHumanCli().parseAsync(argv)
}
